import axios from 'axios';
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config';

import { formatCurrency } from '../../utils/currency/tools';
import { getApiKey as getBootstrappedApiKey } from '../../utils/bootstrap';

export const apiClient = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * =========================
 * API KEY (BOOTSTRAP)
 * =========================
 */
apiClient.interceptors.request.use((request) => {
  const apiKey = getBootstrappedApiKey?.();

  if (apiKey) {
    request.headers = {
      ...request.headers,
      'X-API-KEY': apiKey
    };
  }

  return request;
});

export const getApiEndpoint = async () => {
  const environment = process.env.REACT_APP_NODE_ENV;

  // 1. Development shortcut
  if (environment === 'development') {
    return 'http://localhost:8080/api';
  }

  // 2. Remote config
  const config = getRemoteConfig();

  config.settings = {
    minimumFetchIntervalMillis: 3600000
  };

  await fetchAndActivate(config);

  const remote = getValue(
      config,
      `${environment}_api_endpoint`
  ).asString();

  // 3. Bootstrap fallback (optional override)
  const bootstrapped = null; // keep consistent unless you expose endpoint in bootstrap

  return remote || bootstrapped || 'https://indigas-ams-api-staging-3821bdabfd7f.herokuapp.com/api';
};

/**
 * =========================
 * CREATE GET REQUEST (UNCHANGED STRUCTURE)
 * =========================
 */
export const createGetRequest = async (user, endpoint) => {
  const environment = process.env.REACT_APP_NODE_ENV;

  const config = getRemoteConfig();

  config.settings = {
    minimumFetchIntervalMillis: 3600000
  };

  await fetchAndActivate(config);

  const apiEndpoint = getValue(
      config,
      `${environment}_api_endpoint`
  ).asString();

  const headers = { 'Content-Type': 'application/json' };

  if (user?.accessToken) {
    headers['Authorization'] = `Bearer ${user.accessToken}`;
  } else {
    const apiKey = getBootstrappedApiKey?.();
    if (apiKey) {
      headers['X-API-KEY'] = apiKey;
    }
  }

  return [
    `${environment === 'development'
        ? 'http://localhost:8080/api'
        : apiEndpoint}${endpoint}`,
    { headers, method: 'GET' }
  ];
};

/**
 * =========================
 * CREATE POST REQUEST (UNCHANGED STRUCTURE)
 * =========================
 */
export const createPostRequest = async (user, endpoint, data) => {
  const config = getRemoteConfig();

  config.settings = {
    minimumFetchIntervalMillis: 3600000
  };

  await fetchAndActivate(config);

  const environment = process.env.REACT_APP_NODE_ENV;

  const apiEndpoint = getValue(
      config,
      `${environment}_api_endpoint`
  ).asString();

  const headers = { 'Content-Type': 'application/json' };

  if (user?.accessToken) {
    headers['Authorization'] = `Bearer ${user.accessToken}`;
  } else {
    const apiKey = getBootstrappedApiKey?.();
    if (apiKey) {
      headers['X-API-KEY'] = apiKey;
    }
  }

  return [
    `${environment === 'development'
        ? 'http://localhost:8080/api'
        : apiEndpoint}${endpoint}`,
    { headers, method: 'POST', data }
  ];
};

/**
 * =========================
 * FILE POST (UNCHANGED)
 * =========================
 */
const postFormData = (url, params) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;

  Object.keys(params).forEach((key) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = btoa(params[key]);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

export const postOrderFormData = async (order) => {
  const config = getRemoteConfig();
  const environment = process.env.REACT_APP_NODE_ENV;

  const apiEndpoint = getValue(
      config,
      `${environment}_payment_api_endpoint`
  ).asString();

  postFormData(apiEndpoint, {
    param_1: order.amount,
    param_2: formatCurrency(order.amount),
    param_3: order.orderNumber,
    param_4: 'false',
    param_5: order.email ?? ''
  });
};

/**
 * =========================
 * AUTH HELPERS
 * =========================
 */
export const initialize = (accessToken) => {
  if (accessToken) {
    apiClient.defaults.headers.common['Authorization'] =
        `Bearer ${accessToken}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

export const clearAccessToken = () => {
  delete apiClient.defaults.headers.common['Authorization'];
};

/**
 * =========================
 * QUERY API (RESTORED - FIX FOR YOUR ERRORS)
 * =========================
 */

const withOptionalAuth = (accessToken) => {
  if (!accessToken) return {};

  return {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  };
};

export const queryGet = async (config, endpoint, accessToken) => {
  const url = `${await fetchBaseUrl(config)}${endpoint}`;
  const response = await apiClient.get(url, withOptionalAuth(accessToken));
  return response?.data;
};

export const queryPost = async (config, endpoint, data, accessToken) => {
  const url = `${await fetchBaseUrl(config)}${endpoint}`;
  const response = await apiClient.post(url, data, withOptionalAuth(accessToken));
  return response?.data;
};

export const queryPut = async (config, endpoint, data, accessToken) => {
  const url = `${await fetchBaseUrl(config)}${endpoint}`;
  const response = await apiClient.put(url, data, withOptionalAuth(accessToken));
  return response?.data;
};

export const queryDelete = async (config, endpoint, id, accessToken, extraParams = {}) => {
  const params = new URLSearchParams({ id: String(id), ...extraParams });
  const url = `${await fetchBaseUrl(config)}${endpoint}?${params}`;
  const response = await apiClient.delete(url, withOptionalAuth(accessToken));
  return response?.data;
};

export const queryGetBlob = async (config, endpoint, accessToken) => {
  const url = `${await fetchBaseUrl(config)}${endpoint}`;
  const response = await apiClient.get(url, {
    ...withOptionalAuth(accessToken),
    responseType: 'blob'
  });
  return response?.data;
};

export const queryFileUpload = async (
    config,
    endpoint,
    file,
    accessToken,
    fieldName = 'file'
) => {
  const url = `${await fetchBaseUrl(config)}${endpoint}`;

  const formData = new FormData();
  formData.append(fieldName, file);

  const response = await apiClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(accessToken && {
        Authorization: `Bearer ${accessToken}`
      })
    }
  });

  return response?.data;
};

/**
 * =========================
 * INTERNAL BASE URL RESOLVER
 * =========================
 */
export const fetchBaseUrl = async (config) => {
  const environment = process.env.REACT_APP_NODE_ENV;

  if (environment === 'development') {
    return 'http://localhost:8080/api';
  }

  config.settings = { minimumFetchIntervalMillis: 3600000 };
  await fetchAndActivate(config);

  return getValue(
      config,
      `${environment}_api_endpoint`
  ).asString();
};